// transformDataRoundNumbersTo is also available in the backend,
// but we do it here for reporting details that need different fix oob logic.
const _ = require('lodash');

/**
 * Recursively transforms specified numeric field properties in a nested data structure (array or object)
 * by rounding them based on a factor (ones, thousands, millions).
 * Ensures immutability by returning a new structure.
 *
 * @param {Array | Object} data The data structure (array or object) to transform.
 * @param {'ones' | 'thousands' | 'millions'} [roundNumbersTo='ones'] The rounding factor name.
 * @param {string[]} [fieldNames=['amount', 'compareAmount', 'total', 'compareTotal']] The field names to round.
 * @returns {any} A new data structure with rounded values, or the original input if it's not an array/object or is null/undefined.
 */
const roundToFactors = {
  ones: 1,
  thousands: 1000,
  millions: 1000000,
};

const transformDataRoundNumbersTo = (
  data,
  roundNumbersTo = 'ones',
  fieldNames = ['amount', 'compareAmount', 'total', 'compareTotal']
) => {
  if (data === null || typeof data !== 'object') {
    return data;
  }

  const factor = roundToFactors[roundNumbersTo] || 1;

  function traverse(item) {
    if (Array.isArray(item)) {
      // Create a new array, map over elements, traverse each one
      return item.map(traverse);
    } else if (typeof item === 'object' && item !== null) {
      // Create a new object
      const newItem = {};
      for (const key in item) {
        // Use hasOwnProperty to avoid iterating over inherited properties
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          if (fieldNames.includes(key) && typeof item[key] === 'number') {
            if (isNaN(item[key]) || item[key] === null) {
              newItem[key] = item[key]; // Keep NaN/null as is
            } else {
              newItem[key] = roundAbs(item[key] / factor);
            }
          } else {
            // For other keys or non-numeric target fields, recursively traverse
            newItem[key] = traverse(item[key]);
          }
        }
      }
      return newItem;
    }
    return item;
  }

  const roundedData = traverse(data);
  return roundedData;
};

const roundAbs = (value) => {
  return value > 0 ? Math.round(value) : -1 * Math.round(Math.abs(value));
};

const floorAbs = (value) => {
  return value < 0 ? -Math.floor(Math.abs(value)) : Math.floor(value);
};

/*
 * Fix out-of-balance error for flat data structures by adjusting records to match expected totals.
 * This is used for report details where data is aggregated rather than hierarchical.
 */
const fixOutOfBalanceFlat = (
  roundedData,
  originalData,
  expectedTotal,
  expectedCompareTotal,
  fieldNames,
  factor,
  hasChildren
) => {
  if (!roundedData || roundedData.length === 0) return roundedData;

  const adjustField = (data, original, expectedTotal, fieldName) => {
    if (_.isNil(expectedTotal)) return data;

    // Calculate current total with proper sign adjustment
    const currentTotal = _.sumBy(data, (o) => {
      const value = o[fieldName] || 0;
      return hasChildren && o.config?.invertCalculationAmount ? -value : value;
    });

    const difference = expectedTotal - currentTotal;

    if (difference === 0) return data;

    // Find the best record to adjust - prefer records with largest rounding residue
    const recordToAdjust = _.reduce(
      data.filter((record) => (record[fieldName] || 0) !== 0),
      (chosen, current, index) => {
        const originalRecord = original[index];
        if (!originalRecord) return chosen;

        const originalValue = originalRecord[fieldName] || 0;
        const roundResidule = roundAbs(originalValue % factor);

        // Choose record with largest rounding residue
        if (!chosen || Math.abs(roundResidule) > Math.abs(chosen.residue)) {
          return {
            record: current,
            index,
            residue: roundResidule,
          };
        }
        return chosen;
      },
      null
    );

    if (recordToAdjust) {
      const originalValue = recordToAdjust.record[fieldName] || 0;
      // Only adjust if it won't change the sign of the value
      if (
        originalValue === 0 ||
        Math.sign(originalValue) === Math.sign(originalValue + difference)
      ) {
        recordToAdjust.record[fieldName] = originalValue + difference;
      }
    }

    return data;
  };

  let adjustedData = _.cloneDeep(roundedData);

  if (fieldNames.includes('amount')) {
    adjustedData = adjustField(
      adjustedData,
      originalData,
      expectedTotal,
      'amount'
    );
  }

  if (fieldNames.includes('compareAmount')) {
    adjustedData = adjustField(
      adjustedData,
      originalData,
      expectedCompareTotal,
      'compareAmount'
    );
  }

  return adjustedData;
};

/*
 * Deprecated. use fixOutOfBalanceFlat instead.
 */
// const fixOutOfBalance = (roundedData, data, fieldNames, factor) => {
//   if (!roundedData || roundedData.length === 0) return roundedData;

//   const traverse = (roundedRows, originalRows, fieldName) => {
//     return _.map(roundedRows, (_row, _idx) => {
//       const originalRow = originalRows[_idx] || {};
//       // If this row doesn't have a numeric total (null/undefined/0), we still need to recurse into its children.
//       const hasNumericTotal = !!_row[fieldName];

//       const _total = _row[fieldName];
//       const _childrenTotal = _.sumBy(_row.children, (_child) =>
//         _child.config.invertCalculationAmount
//           ? -1 * _child[fieldName]
//           : _child[fieldName] || 0
//       );

//       const _difference = _total - _childrenTotal;

//       let updatedChildren = _row.children;
//       let largestRoundResidule = 0;

//       if (
//         hasNumericTotal &&
//         _difference !== 0 &&
//         updatedChildren &&
//         updatedChildren.length > 0
//       ) {
//         const childToAdjust = _.reduce(
//           // filter out children with 0 amount
//           updatedChildren.filter((child) => child[fieldName] !== 0),
//           (chosenChild, curr, childIndex) => {
//             const currOriginal = (originalRow.children || [])[childIndex];
//             if (!currOriginal) return chosenChild;

//             const roundResidule = roundAbs(currOriginal[fieldName] % factor);
//             // If the current child is not rounded and has the largest absolute round residue, add oob to it.
//             // console.debug('curr amount', curr[fieldName], 'original amount', currOriginal[fieldName], 'roundResidule', roundResidule, 'largestRoundResidule', largestRoundResidule);
//             if (
//               curr[fieldName] === floorAbs(currOriginal[fieldName] / factor) &&
//               Math.abs(roundResidule) > Math.abs(largestRoundResidule)
//             ) {
//               largestRoundResidule = roundResidule;
//               return curr;
//             }
//             return chosenChild;
//           },
//           // We have to balance, so default to the first child instead of null
//           updatedChildren[0]
//         );

//         if (childToAdjust) {
//           const oob = childToAdjust.config.invertCalculationAmount
//             ? -1 * _difference
//             : _difference;
//           childToAdjust[fieldName] += oob;
//           // create new children array reference after adjustment
//           updatedChildren = [...updatedChildren];
//         } else {
//           // We should never get here otherwise log the bug.
//           console.error('Cannot adjust out of balance number', _row);
//         }
//       }

//       // After parent adjustment, recurse into each child to ensure their subtrees are balanced
//       if (updatedChildren && updatedChildren.length > 0) {
//         updatedChildren = traverse(
//           updatedChildren,
//           originalRow.children || [],
//           fieldName
//         );
//       }

//       return {..._row, children: updatedChildren};
//     });
//   };

//   // data.reports[0].data is the array of reports. why.
//   let reports = roundedData.reports?.[0]?.data;
//   if (!reports) {
//     return roundedData;
//   }

//   reports = reports.map((roundedReport, i) => {
//     const roundedRows = roundedReport.reportConfig;
//     const originalRows = data.reports?.[0]?.data?.[i]?.reportConfig;

//     let balancedRows = roundedRows;
//     for (const fieldName of fieldNames) {
//       balancedRows = traverse(roundedRows, originalRows, fieldName);
//     }
//     return {...roundedReport, reportConfig: balancedRows};
//   });

//   return {
//     ...roundedData,
//     reports: [
//       {
//         ...roundedData.reports?.[0],
//         data: reports,
//       },
//     ],
//   };
// };

module.exports = {
  roundToFactors,
  transformDataRoundNumbersTo,
  roundAbs,
  floorAbs,
  fixOutOfBalanceFlat,
};
